import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { ActivityFeedItemSchema } from '../activity/activity-feed.schemas';
import {
  ActivityRecordCommunicationTypeSchema,
  ActivityRecordKindSchema,
  AppointmentTypeSchema,
} from '../activity/activity-records.schemas';
import { LeadListItemSchema, LeadStatusSchema } from '../lead/leads.schemas';
import { ProspectPhaseSchema, ProspectSourceSchema } from './prospects.schemas';
import { DealAttentionFlagSchema, DealPhaseSchema, DealStatusSchema, type DealPhase } from './deals.schemas';

export const DASHBOARD_PERIODS = ['LAST_7_DAYS', 'LAST_30_DAYS', 'LAST_90_DAYS', 'ALL_TIME'] as const;
export const DashboardPeriodSchema = z.enum(DASHBOARD_PERIODS);
export type DashboardPeriod = z.infer<typeof DashboardPeriodSchema>;
export const DEFAULT_DASHBOARD_PERIOD: DashboardPeriod = 'LAST_30_DAYS';
/** Rolling window length in days; `null` is „Gesamt" — no lower bound. */
export const DASHBOARD_PERIOD_DAYS: Record<DashboardPeriod, number | null> = {
  LAST_7_DAYS: 7,
  LAST_30_DAYS: 30,
  LAST_90_DAYS: 90,
  ALL_TIME: null,
};

export const DASHBOARD_MODES = ['STANDORT', 'OWN', 'CHOOSE_STANDORT'] as const;
export const DashboardScopeSchema = z.object({
  mode: z.enum(DASHBOARD_MODES),
  // The Standort the figures cover. Null for OWN across every visible Standort, and for CHOOSE_STANDORT.
  standortId: z.uuid().nullable(),
});

export const DashboardPathParamsSchema = z.object({ workspaceId: z.uuid() });
export const DashboardQuerySchema = z.object({ standortId: z.uuid().optional() });
export const DashboardFunnelQuerySchema = DashboardQuerySchema.extend({ period: DashboardPeriodSchema.optional() });

/**
 * Where a figure drills down to (AC 6): the target list plus the exact filter the figure was counted with.
 * All six targets are declared here, in the first slice, so an open browser tab on an older bundle
 * never meets a link variant its schema does not know. Later slices only start emitting them.
 */
export const DashboardLinkSchema = z.discriminatedUnion('target', [
  z.object({
    target: z.literal('OWNER_LEADS'),
    standortId: z.uuid().nullable(),
    statuses: z.array(LeadStatusSchema),
    receivedFrom: z.iso.datetime().nullable(),
    receivedTo: z.iso.datetime().nullable(),
    assignedToMe: z.boolean(),
    responsibleMemberIds: z.array(z.uuid()),
  }),
  z.object({
    target: z.literal('DEAL_OVERVIEW'),
    standortId: z.uuid().nullable(),
    assigneeMemberIds: z.array(z.uuid()),
    phases: z.array(DealPhaseSchema),
    statuses: z.array(DealStatusSchema),
    attentionFlags: z.array(DealAttentionFlagSchema),
    createdFrom: z.iso.datetime().nullable(),
    enteredPhase: DealPhaseSchema.nullable(),
    enteredFrom: z.iso.datetime().nullable(),
    withoutValuation: z.boolean(),
    unassigned: z.boolean(),
    // Exact deal set, for a figure no semantic filter can express (the Team table's „Courtage aus Aufträgen & Online",
    // IMM-1136.6). [] = no id filter. Bounded by one member's deals in one Standort, so it fits a URL.
    dealIds: z.array(z.uuid()),
  }),
  z.object({
    target: z.literal('ACTIVITIES'),
    standortId: z.uuid().nullable(),
    // Set: the overdue view (open, due before this instant). Null: the list's own upcoming view.
    dueBefore: z.iso.datetime().nullable(),
    assigneeMemberIds: z.array(z.uuid()),
  }),
  z.object({
    target: z.literal('PROPERTIES'),
    propertyIds: z.array(z.uuid()),
  }),
  z.object({
    target: z.literal('SALES_PIPELINE'),
    standortId: z.uuid().nullable(),
    assignedToMe: z.boolean(),
  }),
  z.object({
    target: z.literal('MEMBERS'),
    memberIds: z.array(z.uuid()),
  }),
]);
export type DashboardLink = z.infer<typeof DashboardLinkSchema>;
export type OwnerLeadsLink = Extract<DashboardLink, { target: 'OWNER_LEADS' }>;
export type DealOverviewLink = Extract<DashboardLink, { target: 'DEAL_OVERVIEW' }>;
export type ActivitiesLink = Extract<DashboardLink, { target: 'ACTIVITIES' }>;
export type PropertiesLink = Extract<DashboardLink, { target: 'PROPERTIES' }>;
export type SalesPipelineLink = Extract<DashboardLink, { target: 'SALES_PIPELINE' }>;
export type MembersLink = Extract<DashboardLink, { target: 'MEMBERS' }>;

export const DashboardFigureSchema = z.object({ value: z.number().int().nonnegative(), link: DashboardLinkSchema });
export type DashboardFigure = z.infer<typeof DashboardFigureSchema>;

export const DashboardLeadStageSchema = z.object({
  open: DashboardFigureSchema,
  received: DashboardFigureSchema,
  converted: DashboardFigureSchema,
  // The open leads split by status; NEW + CONTACTED = open.
  byStatus: z.object({ NEW: DashboardFigureSchema, CONTACTED: DashboardFigureSchema }),
  // Whole percent; null when nothing was received in the period.
  conversionRate: z.number().int().min(0).max(100).nullable(),
});

/** The funnel's two deal stages, split at „Auftrag erhalten". Array order is pipeline order. */
export const ACQUISITION_PHASES = [
  'IN_EXCHANGE',
  'APPOINTMENT_SCHEDULED',
  'APPOINTMENT_COMPLETED',
  'VALUATION_COMPLETED',
] as const satisfies readonly DealPhase[];
export const MANDATE_PHASES = [
  'MANDATE_RECEIVED',
  'CONTRACT_SENT',
  'CONTRACT_RECEIVED',
  'EXPOSE_REVIEW',
] as const satisfies readonly DealPhase[];

export const DashboardAmountSchema = z.object({ amountEur: z.number().int().nonnegative(), link: DashboardLinkSchema });

export const DashboardDealStageSchema = z.object({
  deals: DashboardFigureSchema,
  courtage: DashboardAmountSchema,
  phases: z.array(z.object({ phase: DealPhaseSchema, deals: DashboardFigureSchema, courtage: DashboardAmountSchema })),
  // Acquisition: deals created in the period · mandates: Aufträge received in the period.
  intake: DashboardFigureSchema,
});
export type DashboardDealStage = z.infer<typeof DashboardDealStageSchema>;

export const DASHBOARD_ONLINE_PREVIEW_SIZE = 3;

export const DashboardOnlineObjectSchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  propertyLabel: z.string(),
  // Null when the Objekt has no primary assignee, or the assignee is no longer an active member.
  responsibleMember: z.object({ memberId: refersTo('member', z.uuid()), name: z.string() }).nullable(),
  onlineSince: z.iso.datetime().nullable(),
  activeProspects: z.number().int().nonnegative(),
});
export type DashboardOnlineObject = z.infer<typeof DashboardOnlineObjectSchema>;

export const DashboardOnlineStageSchema = z.object({
  properties: DashboardFigureSchema,
  courtage: DashboardAmountSchema,
  wentOnline: DashboardFigureSchema,
  // The Objekte online longest, earliest first.
  longest: z.array(DashboardOnlineObjectSchema).max(DASHBOARD_ONLINE_PREVIEW_SIZE),
  // The online Objekte beyond `longest`; links to the whole Online list.
  more: DashboardFigureSchema,
});
export type DashboardOnlineStage = z.infer<typeof DashboardOnlineStageSchema>;

export type DashboardLeadStage = z.infer<typeof DashboardLeadStageSchema>;

export const GetDashboardFunnelResponseSchema = z.object({
  scope: DashboardScopeSchema,
  period: DashboardPeriodSchema,
  // Null exactly when scope.mode is CHOOSE_STANDORT — no figure is read.
  funnel: z
    .object({
      leads: DashboardLeadStageSchema,
      acquisition: DashboardDealStageSchema,
      mandates: DashboardDealStageSchema,
      online: DashboardOnlineStageSchema,
    })
    .nullable(),
});
export type GetDashboardFunnelResponse = z.infer<typeof GetDashboardFunnelResponseSchema>;

export const DASHBOARD_TABLE_ROWS = 5;

export const DashboardCurrentStateSchema = z.object({
  kind: ActivityRecordKindSchema,
  communicationType: ActivityRecordCommunicationTypeSchema.nullable(),
  appointmentType: AppointmentTypeSchema.nullable(),
  occurredAt: z.iso.datetime(),
  content: z.string(),
});
export type DashboardCurrentState = z.infer<typeof DashboardCurrentStateSchema>;

export const DashboardLeadRowSchema = z.object({
  // The list's own row, rendered by the list's own cell helpers (BL-082).
  lead: LeadListItemSchema,
  // Null → „Noch kein Kontakt".
  currentState: DashboardCurrentStateSchema.nullable(),
});
export type DashboardLeadRow = z.infer<typeof DashboardLeadRowSchema>;

export const DashboardInquiryRowSchema = z.object({
  prospectId: refersTo('prospect', z.uuid()),
  contactId: refersTo('contact', z.uuid()),
  contactName: z.string().nullable(),
  propertyId: refersTo('property', z.uuid()),
  propertyLabel: z.string().nullable(),
  source: ProspectSourceSchema.nullable(),
  phase: ProspectPhaseSchema,
  createdAt: z.iso.datetime(),
  currentState: DashboardCurrentStateSchema.nullable(),
});
export type DashboardInquiryRow = z.infer<typeof DashboardInquiryRowSchema>;

export const GetDashboardNewRecordsResponseSchema = z.object({
  scope: DashboardScopeSchema,
  newRecords: z
    .object({
      leads: z.object({ items: z.array(DashboardLeadRowSchema), total: DashboardFigureSchema }),
      inquiries: z.object({ items: z.array(DashboardInquiryRowSchema), total: DashboardFigureSchema }),
    })
    .nullable(), // null exactly in CHOOSE_STANDORT
});
export type GetDashboardNewRecordsResponse = z.infer<typeof GetDashboardNewRecordsResponseSchema>;

/** How many records an attention row previews under its count (IMM-1218). */
export const DASHBOARD_ATTENTION_PREVIEW_SIZE = 3;

/** An attention row's preview: its most urgent records, and how many more stand behind the row. */
const attentionPreviewOf = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item).max(DASHBOARD_ATTENTION_PREVIEW_SIZE),
    // Records behind the row that `items` does not show; drives „+ {more} weitere".
    more: z.number().int().nonnegative(),
  });

export const DashboardWaitingLeadSchema = z.object({
  leadId: refersTo('lead', z.uuid()),
  displayName: z.string(),
  receivedAt: z.iso.datetime(),
});
export type DashboardWaitingLead = z.infer<typeof DashboardWaitingLeadSchema>;

export const DASHBOARD_DATA_GAPS = ['WITHOUT_VALUATION', 'UNASSIGNED'] as const;
export const DashboardDataGapSchema = z.enum(DASHBOARD_DATA_GAPS);
export type DashboardDataGap = z.infer<typeof DashboardDataGapSchema>;

export const DashboardFlaggedDealSchema = z.object({
  dealId: refersTo('deal', z.uuid()),
  // Null when the Objekt cannot be read; the deal still opens.
  propertyLabel: z.string().nullable(),
  flag: DealAttentionFlagSchema.exclude(['ON_TRACK']),
});
export type DashboardFlaggedDeal = z.infer<typeof DashboardFlaggedDealSchema>;

export const DashboardGapDealSchema = z.object({
  dealId: refersTo('deal', z.uuid()),
  propertyLabel: z.string().nullable(),
  gaps: z.array(DashboardDataGapSchema).min(1),
});
export type DashboardGapDeal = z.infer<typeof DashboardGapDealSchema>;

export const GetDashboardAttentionResponseSchema = z.object({
  scope: DashboardScopeSchema,
  attention: z
    .object({
      overdueTasks: z.object({
        count: DashboardFigureSchema,
        oldestDueAt: z.iso.datetime().nullable(),
        // Oldest due first: the Aktivitäten feed's overdue window, in the feed's own row shape (BL-082).
        preview: attentionPreviewOf(ActivityFeedItemSchema),
      }),
      leadsWithoutResponse: z.object({
        count: DashboardFigureSchema,
        oldestReceivedAt: z.iso.datetime().nullable(),
        // Longest waiting first: the „ohne Reaktion" link's rows, oldest received first.
        preview: attentionPreviewOf(DashboardWaitingLeadSchema),
      }),
      dealsNeedingAction: z.object({
        count: DashboardFigureSchema,
        byFlag: z.object({
          OVERDUE: z.number().int().nonnegative(),
          NO_NEXT_STEP: z.number().int().nonnegative(),
          TOO_LONG_IN_PHASE: z.number().int().nonnegative(),
        }),
        // The Deal-Überblick's order: flag rank, then the stalest first (IMM-1218).
        preview: attentionPreviewOf(DashboardFlaggedDealSchema),
      }),
      dataGaps: z.object({
        withoutValuation: DashboardFigureSchema,
        // Null in OWN scope: an own deal always has a responsible member.
        unassigned: DashboardFigureSchema.nullable(),
        // One item per deal, in the Deal-Überblick's order; `more` counts distinct deals.
        preview: attentionPreviewOf(DashboardGapDealSchema),
      }),
      upcomingTasks: z.object({ items: z.array(ActivityFeedItemSchema), link: DashboardLinkSchema }),
    })
    .nullable(), // null exactly in CHOOSE_STANDORT
});
export type GetDashboardAttentionResponse = z.infer<typeof GetDashboardAttentionResponseSchema>;

/** „Ohne Reaktion": a lead still Neu this long after it arrived. */
export const LEAD_RESPONSE_WINDOW_HOURS = 24;
export const DASHBOARD_UPCOMING_TASKS = 6;

export const DASHBOARD_SETUP_ITEMS = ['MEMBER_ONBOARDING', 'MAILBOX'] as const;
export const DASHBOARD_SETUP_MEMBER_STATES = ['OPEN', 'RECONNECT'] as const;
export const DASHBOARD_SETUP_ACTIONS = ['MEMBERS', 'MY_PROFILE', 'MY_MAILBOX'] as const;

export const DashboardSetupItemSchema = z.object({
  key: z.enum(DASHBOARD_SETUP_ITEMS),
  done: z.number().int().nonnegative(),
  total: z.number().int().positive(),
  // value = total − done; link = MEMBERS { memberIds: open member ids }
  open: DashboardFigureSchema,
  openMembers: z.array(
    z.object({
      memberId: refersTo('member', z.uuid()),
      name: z.string(),
      state: z.enum(DASHBOARD_SETUP_MEMBER_STATES),
    }),
  ),
  action: z.enum(DASHBOARD_SETUP_ACTIONS),
});
export type DashboardSetupItem = z.infer<typeof DashboardSetupItemSchema>;
export type DashboardSetupAction = DashboardSetupItem['action'];

export const GetDashboardSetupResponseSchema = z.object({
  scope: DashboardScopeSchema,
  // Null in CHOOSE_STANDORT; otherwise only the items with something open — [] means the block is gone.
  setup: z.object({ items: z.array(DashboardSetupItemSchema) }).nullable(),
});
export type GetDashboardSetupResponse = z.infer<typeof GetDashboardSetupResponseSchema>;

export const DashboardTeamRowSchema = z.object({
  memberId: refersTo('member', z.uuid()).nullable(), // null = the „Ohne Zuständigkeit" row
  name: z.string().nullable(), // null on the „Ohne Zuständigkeit" row
  activeDeals: DashboardFigureSchema,
  pipelineCourtage: DashboardAmountSchema,
  mandateAndOnlineCourtage: DashboardAmountSchema,
  overdueTasks: DashboardFigureSchema.nullable(), // null on the „Ohne Zuständigkeit" row
  leadsWithoutResponse: DashboardFigureSchema.nullable(),
});
export type DashboardTeamRow = z.infer<typeof DashboardTeamRowSchema>;

export const GetDashboardTeamResponseSchema = z.object({
  scope: DashboardScopeSchema,
  team: z.object({ rows: z.array(DashboardTeamRowSchema) }).nullable(), // null unless STANDORT
});
export type GetDashboardTeamResponse = z.infer<typeof GetDashboardTeamResponseSchema>;
